import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, ScrollText } from "lucide-react";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminOnly } from "@/components/admin/AdminOnly";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { adminListAudit } from "@/lib/admin-security.functions";

export const Route = createFileRoute("/_authenticated/admin/audit")({
  head: () => ({ meta: [{ title: "Audit log — Admin" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <AdminOnly>
      <AdminNav />
      <AuditPage />
    </AdminOnly>
  ),
});

function AuditPage() {
  const run = useServerFn(adminListAudit);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-audit"],
    queryFn: () => run({ data: { limit: 300 } }),
  });

  const rows = (data?.audit ?? []) as Array<{
    id: string; admin_id: string | null; action: string;
    target_type: string | null; target_id: string | null;
    before: unknown; after: unknown; created_at: string;
    profiles: { email: string; full_name: string | null } | null;
  }>;

  return (
    <div className="space-y-5">
      <AdminPageHeader
        icon={ScrollText}
        title="Audit log"
        subtitle="Every admin action is recorded here for accountability."
        tone="text-violet-500"
        glow="hsl(268 84% 65% / 0.30)"
      />


      <div className="overflow-hidden rounded-2xl border bg-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
            <Loader2 className="me-2 h-4 w-4 animate-spin" /> Loading audit log…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground">No admin actions logged yet.</div>
        ) : (
          <ul className="divide-y text-sm">
            {rows.map((r) => (
              <li key={r.id} className="px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-mono uppercase text-primary">{r.action}</span>
                    <span className="text-xs">
                      by <span className="font-medium">{r.profiles?.full_name ?? r.profiles?.email ?? "system"}</span>
                    </span>
                    {r.target_type && (
                      <span className="text-[11px] text-muted-foreground">
                        · {r.target_type}
                        {r.target_id ? ` ${r.target_id.slice(0, 8)}…` : ""}
                      </span>
                    )}
                  </div>
                  <span className="shrink-0 text-[11px] text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>
                </div>
                {Boolean(r.before || r.after) && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-[11px] text-muted-foreground hover:text-foreground">Diff</summary>
                    <pre className="mt-1 overflow-auto rounded bg-muted/60 p-2 text-[10px] leading-snug">{String(JSON.stringify({ before: r.before, after: r.after }, null, 2))}</pre>
                  </details>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
